/**
 * Cliente de la API. Añade las cabeceras de seguridad y del negocio activo,
 * y convierte los errores en ErrorApi con el mensaje en español.
 */

const CLAVE_NEGOCIO = 'gp:negocio';
/** Copia en memoria: disponible aunque el navegador bloquee el almacenamiento local. */
let negocioEnMemoria: string | null = null;

export function negocioActivo(): string | null {
  if (negocioEnMemoria) return negocioEnMemoria;
  try {
    return localStorage.getItem(CLAVE_NEGOCIO);
  } catch {
    return null;
  }
}

export function fijarNegocioActivo(id: string | null) {
  negocioEnMemoria = id;
  try {
    if (id) localStorage.setItem(CLAVE_NEGOCIO, id);
    else localStorage.removeItem(CLAVE_NEGOCIO);
  } catch {
    /* almacenamiento no disponible: se pedirá de nuevo */
  }
}

export class ErrorApi extends Error {
  constructor(
    public readonly estado: number,
    mensaje: string,
    public readonly datos: { codigo?: string; campos?: { campo: string; mensaje: string }[]; [k: string]: unknown } = {},
  ) {
    super(mensaje);
  }
  get codigo() {
    return this.datos.codigo;
  }
}

async function peticion<T>(metodo: string, ruta: string, cuerpo?: unknown, opciones: { formulario?: FormData } = {}): Promise<T> {
  const cabeceras: Record<string, string> = {};
  if (metodo !== 'GET') cabeceras['X-GestionPYME'] = '1';
  const negocio = negocioActivo();
  if (negocio) cabeceras['X-Negocio-Id'] = negocio;
  let body: BodyInit | undefined;
  if (opciones.formulario) body = opciones.formulario;
  else if (cuerpo !== undefined) {
    cabeceras['Content-Type'] = 'application/json';
    body = JSON.stringify(cuerpo);
  }
  const res = await fetch(`/api${ruta}`, { method: metodo, headers: cabeceras, body, credentials: 'same-origin' });
  const tipo = res.headers.get('content-type') ?? '';
  const datos = tipo.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    const mensaje =
      datos?.campos?.length > 0
        ? `${datos.mensaje}: ${datos.campos.map((c: { campo: string; mensaje: string }) => `${c.campo} – ${c.mensaje}`).join('; ')}`
        : (datos?.mensaje ?? `Error ${res.status}`);
    throw new ErrorApi(res.status, mensaje, datos ?? {});
  }
  return datos as T;
}

export const api = {
  get: <T>(ruta: string) => peticion<T>('GET', ruta),
  post: <T>(ruta: string, cuerpo: unknown = {}) => peticion<T>('POST', ruta, cuerpo),
  put: <T>(ruta: string, cuerpo: unknown) => peticion<T>('PUT', ruta, cuerpo),
  delete: <T>(ruta: string) => peticion<T>('DELETE', ruta),
  subir: <T>(ruta: string, formulario: FormData) => peticion<T>('POST', ruta, undefined, { formulario }),
};

/** Descarga un archivo de la API (con las cabeceras de sesión) y lo guarda en el equipo. */
export async function descargar(ruta: string, nombrePorDefecto: string): Promise<void> {
  const cabeceras: Record<string, string> = {};
  const negocio = negocioActivo();
  if (negocio) cabeceras['X-Negocio-Id'] = negocio;
  const res = await fetch(`/api${ruta}`, { headers: cabeceras, credentials: 'same-origin' });
  if (!res.ok) {
    const datos = await res.json().catch(() => null);
    throw new ErrorApi(res.status, datos?.mensaje ?? `Error ${res.status}`, datos ?? {});
  }
  const disposicion = res.headers.get('content-disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(disposicion) ?? /filename="([^"]+)"/.exec(disposicion);
  const nombre = m ? decodeURIComponent(m[1]!) : nombrePorDefecto;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Abre un documento en otra pestaña (se descarga con fetch para enviar la cabecera del negocio). */
export async function abrirDocumento(id: string): Promise<void> {
  const cabeceras: Record<string, string> = {};
  const negocio = negocioActivo();
  if (negocio) cabeceras['X-Negocio-Id'] = negocio;
  const res = await fetch(`/api/documentos/${id}/archivo`, { headers: cabeceras, credentials: 'same-origin' });
  if (!res.ok) throw new ErrorApi(res.status, 'No se pudo abrir el documento');
  const url = URL.createObjectURL(await res.blob());
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
